import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireAdminOrRop } from "@/auth/middleware";
import { sanitizeForSeroval } from "@/server/seroval-safe";
import {
  assistantIssue,
  invariantIssue,
  issueNumber,
  screenName,
  textIssue,
} from "./complaint-issue";
import {
  listComplaints,
  markComplaintReported,
  openComplaintsCount,
  readComplaint,
  resolveByIssue,
  resolveComplaint,
  unreportedCount,
  unresolvedWithIssue,
  type ComplaintRow,
} from "./complaints-store";
import { closedIssues, openIssue } from "./github-issues";

/**
 * Экран разбора жалоб.
 *
 * До 21.09 жалобу можно было прочитать одним способом: зайти по ssh на
 * сервер и написать запрос в psql. Снимок при этом лежал байтами в колонке —
 * его ещё надо было вынуть и раскодировать. Данные агентства уехать наружу
 * не могут (решение 19.09), значит, читать их надо здесь.
 *
 * Читают руководитель и администратор: это разбор работы системы, а не работа
 * менеджера. RLS в базе говорит то же самое (`complaints_select`), так что
 * менеджер, дошедший до адреса руками, увидит только свои жалобы.
 */

export interface ComplaintsView {
  rows: ComplaintRow[];
  /** записаны, но указатель не ушёл: канал молчит, чинить некому */
  unreported: number;
  /** сколько жалоб закрылось прямо сейчас — по задачам у разработчика */
  closedNow: number;
}

/**
 * Сверка с задачами у разработчика продукта.
 *
 * Решение владельца 22.09: закрывают жалобу там, где чинят, — в GitHub, — а
 * портал держит то, чего в задаче быть не может: снимок, текст экрана,
 * заказчика и суммы. Значит, отметка «разобрано» должна приходить оттуда
 * сама, иначе одну и ту же жалобу закрывают дважды, а счётчик зовёт чинить
 * починенное.
 *
 * Сверяем при открытии экрана, а не фоном: планировщика в сервисе нет, и
 * состояние должно сходиться тогда, когда на него смотрят. В одну сторону:
 * закрытая задача помечает жалобу разобранной, но снятая в портале отметка
 * задачу не открывает — заново открыть её может тот, кто чинит, и это его
 * решение.
 *
 * Молчащий GitHub экран не ломает: не ответил — разбор остаётся ручным.
 */
async function syncClosedIssues(): Promise<number> {
  const open = await unresolvedWithIssue();
  if (open.length === 0) return 0;

  const byNumber = new Map<number, number>();
  for (const row of open) {
    const n = issueNumber(row.issueUrl);
    if (n !== undefined) byNumber.set(n, row.id);
  }
  if (byNumber.size === 0) return 0;

  const closed = await closedIssues([...byNumber.keys()]);
  const ids = [...closed].map((n) => byNumber.get(n)!).filter(Boolean);
  const n = await resolveByIssue(ids);
  if (n > 0) console.info(`[complaints] закрыто задачами в GitHub: ${n}`);
  return n;
}

const _loadComplaints = createServerFn({ method: "GET" })
  .middleware([requireAdminOrRop])
  .handler(async (): Promise<ComplaintsView> => {
    // Сначала сверка, потом чтение: иначе экран покажет список, который сам
    // же только что сделал неверным.
    const closedNow = await syncClosedIssues();
    return sanitizeForSeroval("loadComplaints", {
      rows: await listComplaints(),
      unreported: await unreportedCount(),
      closedNow,
    });
  });

/**
 * Разбор жалобы читается как есть: у ассистента там разговор и суммы, у
 * текста — выделение и адрес. Форма разная, и тип описывает ровно то, что
 * переживает провод, — иначе сериализация ответа не проходит по типам.
 */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [k: string]: JsonValue };

export interface ComplaintDetails {
  payload: Record<string, JsonValue>;
  /** снимок как data-URL; пусто — не собрался в момент жалобы */
  screenshot: string | null;
}

/**
 * Подробности отдельным запросом — из-за снимка.
 *
 * Снимок «Расчёта» весит под мегабайт, и девять таких в одном списке — это
 * десять мегабайт на открытие экрана. Здесь их просят по одной, когда жалобу
 * открывают.
 */
const _loadComplaint = createServerFn({ method: "POST" })
  .middleware([requireAdminOrRop])
  .inputValidator((d: unknown) => z.object({ id: z.number().int().positive() }).parse(d))
  .handler(async ({ data }): Promise<ComplaintDetails | null> => {
    const row = await readComplaint(data.id);
    if (!row) return null;
    // `sanitizeForSeroval` сужает `unknown` до `{}` — payload здесь
    // произвольный по устройству: у ассистента разговор, у текста выделение.
    return sanitizeForSeroval("loadComplaint", {
      payload: row.payload,
      screenshot: row.screenshot,
    }) as ComplaintDetails;
  });

const _setComplaintResolved = createServerFn({ method: "POST" })
  .middleware([requireAdminOrRop])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.number().int().positive(),
        resolved: z.boolean(),
        resolution: z.string().max(1000).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<{ ok: true }> => {
    await resolveComplaint(data.id, data.resolution ?? null, data.resolved);
    return { ok: true };
  });

/**
 * Дослать указатель.
 *
 * Пустая отметка об отправке значит, что задача не завелась: токен протух,
 * GitHub не ответил, канал выключен. Раньше единственным выходом было завести
 * задачу руками, глядя в psql. Текст собирается тот же самый и из того же
 * места — заново по записанной жалобе.
 */
const _resendComplaint = createServerFn({ method: "POST" })
  .middleware([requireAdminOrRop])
  .inputValidator((d: unknown) => z.object({ id: z.number().int().positive() }).parse(d))
  .handler(async ({ data }): Promise<{ ok: boolean; url?: string; error?: string }> => {
    const row = await readComplaint(data.id);
    if (!row) return { ok: false, error: "Жалоба не найдена" };

    const p = row.payload as Record<string, string | undefined>;
    const text =
      row.kind === "assistant"
        ? assistantIssue(data.id, {
            screen: screenName(p.screen, ""),
            estimateId: row.estimateId ?? "",
            messageId: p.messageId ?? "",
            ask: p.ask ?? "",
            note: p.note,
            fallback: Boolean(row.payload.fallback),
          })
        : row.kind === "text"
          ? textIssue(data.id, {
              screen: screenName(p.screen, p.path ?? ""),
              path: p.path ?? "",
              estimateId: row.estimateId ?? undefined,
              note: p.note,
            })
          : invariantIssue(data.id, {
              estimateId: row.estimateId ?? "",
              codes: row.codes,
              projectType: p.projectType,
              taxSchemeId: p.taxSchemeId,
            });

    const labels = row.kind === "invariant" ? ["инвариант"] : ["жалоба"];
    const issue = await openIssue(text.title, text.body, labels);
    if (!issue.ok) return { ok: false, error: issue.error };

    await markComplaintReported(data.id, issue.url);
    return { ok: true, url: issue.url };
  });

/** Цифра рядом с пунктом меню: сколько жалоб ждёт разбора. */
const _countOpenComplaints = createServerFn({ method: "GET" })
  .middleware([requireAdminOrRop])
  .handler(async (): Promise<{ open: number }> => ({ open: await openComplaintsCount() }));

export const loadComplaints = _loadComplaints;
export const loadComplaint = _loadComplaint;
export const setComplaintResolved = _setComplaintResolved;
export const resendComplaint = _resendComplaint;
export const countOpenComplaints = _countOpenComplaints;
